import React, { useRef, useState } from "react";

import Collapse from "@mui/material/Collapse";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import useRssLinksSettings from "@app/analytics/hooks/settings/useRssLinksSettings";
import { defaultOnSuccess } from "@app/analytics/hooks/settings/useUserSettings";
import * as Events from "@app/analytics/tracking/events";
import { getDisplayUrl } from "@app/common/utils";
import Config from "@app/config";

import RuiButton from "@rui/Button";
import CopyButton from "@rui/CopyButton";
import Stack, { Row } from "@rui/Stack";
import TextField from "@rui/TextField";

const MAX_VANITY_ID_LENGTH = 100;
// limit vanity ids to alphanumeric characters and "-", "_", or "+"
const VANITY_ID_REGEX = /[^\w-_+]/g;

const Button = styled(RuiButton)({
  height: "40px",
  borderRadius: "6px",
});

const CustomUrl = () => {
  const { rssLinkSettings, rootSchedulingLinkUrl, updateRssLinkSettings } =
    useRssLinksSettings();

  const [isEditing, setIsEditing] = useState(false);
  const [updatedVanityId, setUpdatedVanityId] = useState<string | null>(null);
  const [vanityIdError, setVanityIdError] = useState<string | null>(null);

  const textFieldRef = useRef<HTMLInputElement | null>(null);

  const handleSaveVanityId = () => {
    if (updatedVanityId == null) return;
    updateRssLinkSettings.mutate(
      { vanityId: updatedVanityId },
      {
        onSuccess: () => {
          defaultOnSuccess();
          setIsEditing(false);
        },
        onError: () => {
          setVanityIdError(`"${updatedVanityId}" already claimed`);
        },
        onSettled: () => setUpdatedVanityId(null),
      }
    );
  };

  const handleVanityIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdatedVanityId(event.target.value.replace(VANITY_ID_REGEX, ""));
    setVanityIdError(null);
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    textFieldRef.current?.focus();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setUpdatedVanityId(null);
    setVanityIdError(null);
  };

  return (
    <Stack sx={{ px: 1 }} spacing={1.5}>
      <Typography variant="h3">Custom URL</Typography>
      <Typography variant="bodySecondaryReg" color="shades.neutral.600">
        Personalize your Smart Scheduler URL. Updating this means that all
        previous links used for scheduling will no longer work.
      </Typography>
      <Stack>
        <Row variant="left" spacing={1}>
          <Typography variant="bodySecondaryReg">
            {getDisplayUrl(Config.RSS_LINKS_URL)}/
          </Typography>
          <TextField
            sx={{ "& .MuiInputBase-root": { height: "40px" } }}
            value={updatedVanityId ?? rssLinkSettings.vanityId}
            onChange={handleVanityIdChange}
            inputProps={{
              readOnly: !isEditing,
              maxLength: MAX_VANITY_ID_LENGTH,
            }}
            ref={textFieldRef}
          />
          <Collapse in={isEditing} orientation="horizontal" unmountOnExit>
            <Row spacing={1}>
              <Button
                disabled={!updatedVanityId}
                onClick={handleSaveVanityId}
                loading={
                  updatedVanityId != null && updateRssLinkSettings.isPending
                }
              >
                Save
              </Button>
              <Button
                variant="outlined"
                sx={{ borderColor: "shades.neutral.100" }}
                onClick={handleCancelEdit}
              >
                Cancel
              </Button>
            </Row>
          </Collapse>
          <Collapse in={!isEditing} orientation="horizontal" unmountOnExit>
            <Row spacing={1}>
              <CopyButton
                copyValue={rootSchedulingLinkUrl}
                onClick={() =>
                  Events.copySchedulingLink({ source: "user_settings" })
                }
                renderButton={({ onClick }, copied) => (
                  <Button
                    onClick={onClick}
                    sx={{
                      bgcolor: copied ? "shades.meadow.600" : undefined,
                      "&:hover": {
                        bgcolor: copied ? "shades.meadow.600" : undefined,
                      },
                      minWidth: "85px",
                    }}
                  >
                    {copied ? "Copied!" : "Copy"}
                  </Button>
                )}
              />
              <Button
                variant="outlined"
                sx={{ borderColor: "shades.neutral.100" }}
                onClick={handleStartEditing}
              >
                Edit
              </Button>
            </Row>
          </Collapse>
        </Row>
        {vanityIdError && (
          <Typography variant="labelReg" color="error.main">
            {vanityIdError}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default CustomUrl;
